// The Mission Panel card's Now zone as pure data (#6745, epic #6552; prototype
// `docs/design/fleet-redesign/prototype.html` jobsHTML `:2180-2225`, jobRes `:2169-2179`,
// nowToggle `:2279-2283`, the zone header `:2459`): every job a machine holds for this
// account (`UtilBox.jobs`), its zone count, its capability groups in first-seen order with
// their summed usage, each job's usage row and tag, the folded summary line, and the card's
// epics folded from the jobs. `components/machine-card/MachineJobs.tsx` renders it; nothing
// here reads the url, a signal or the clock (the caller's `nowMs`).
//
// REAL READINGS ONLY (owner ruling G3; brief.md V9 item 22). A job's usage row is its
// machine's beat, never an estimate: a null reading shows "-", is never amber and is left
// out of a group's sum. A machine whose beat carries no per-job usage (`reports_job_usage`
// not true: an older runner, or no live check-in) shows its jobs with no usage row at all,
// never "Measuring usage…".

import { MACHINE_JOB_STATES, type MachineJobState, type PromptRole } from '@developerz/domain';
import type { Translator } from '@developerz/i18n';
import { OFFLINE_STATUSES } from './fleet-machines';
import type { UtilBox, UtilJob } from './fleetUtilization';
import type { CardEpic } from './machine-card';
import { isRunningJob } from './shown-job';
import { decimal, since } from './time';

type Tr = Translator;

/** The machine's jobs; an api older than the field sends none, which reads as no jobs. */
export const boxJobs = (box: Pick<UtilBox, 'jobs'>): readonly UtilJob[] => box.jobs ?? [];

/**
 * The jobs RUNNING on the machine (`assigned` / `in_progress`): what "busy" means. A paused
 * or pinned `pending` job sits on the machine without running, so it never makes a machine
 * or a repo read as working.
 */
export const runningJobs = (box: Pick<UtilBox, 'jobs'>): readonly UtilJob[] =>
  boxJobs(box).filter(isRunningJob);

const KNOWN_STATES: ReadonlySet<string> = new Set(MACHINE_JOB_STATES);

/** The job's state, or null for one this bundle does not know (an api ahead of it). */
export const jobState = (job: Pick<UtilJob, 'state'>): MachineJobState | null =>
  KNOWN_STATES.has(job.state) ? job.state : null;

/**
 * The capability a job's role reads as, one total map (prototype CAPS, `:1314-1324`):
 * `worker` is Coder (CI fixing runs as `worker` too, `pr_babysit`, until the CI Fixer role
 * has its own key). `setup` and the roles no box runs (`architect`, `janitor`) read their
 * own names, so a new role is never blank; they and "Other" (a null role) are not in the
 * prototype, a listed deviation.
 */
export const CAPABILITY_KEY: Readonly<Record<PromptRole, string>> = {
  worker: 'agents.name.coder',
  reviewer: 'agents.name.reviewer',
  scout: 'agents.name.scout',
  devops: 'agents.name.devops',
  triage: 'agents.name.triage',
  setup: 'fleet.machines.job_role_setup',
  architect: 'fleet.machines.job_role_architect',
  janitor: 'fleet.machines.job_role_janitor',
};
const OTHER_KEY = 'fleet.machines.job_role_other';

/** The capability key for a role; a null role, or one this bundle does not know, is "Other". */
export function capabilityKey(role: PromptRole | null): string {
  return role !== null && Object.hasOwn(CAPABILITY_KEY, role) ? CAPABILITY_KEY[role] : OTHER_KEY;
}

/**
 * A job's tag (prototype JOBTAG, `:1336`). A working job carries none. #6753 adds the pause
 * tags, all neutral: `pausing` (a pause requested on a started run), and a `paused` job's
 * reason, `epic_paused` when its epic's own `paused_at` is set (`JOBTAG.epaused`) and
 * `paused` ("Paused by you", `JOBTAG.upaused`) otherwise. An epic whose `paused_at` field is
 * absent (an api older than #6753) claims nothing, so it reads "Paused by you".
 */
export type JobTag = 'starting' | 'last_known' | 'pausing' | 'epic_paused' | 'paused';

export const JOB_TAG_LABEL: Readonly<Record<JobTag, string>> = {
  starting: 'fleet.machines.job_tag_starting',
  last_known: 'fleet.machines.job_tag_last_known',
  pausing: 'fleet.machines.job_tag_pausing',
  epic_paused: 'fleet.machines.job_tag_epic_paused',
  paused: 'fleet.machines.job_tag_paused',
};

export function jobTag(job: Pick<UtilJob, 'state' | 'epic'>): JobTag | null {
  const state = jobState(job);
  if (state === 'paused') return job.epic?.paused_at != null ? 'epic_paused' : 'paused';
  return state === 'starting' || state === 'last_known' || state === 'pausing' ? state : null;
}

const isOffline = (box: Pick<UtilBox, 'verdict'>): boolean =>
  OFFLINE_STATUSES.includes(box.verdict.status);

// ─── Usage ───────────────────────────────────────────────────────────────────

/** CPU and memory turn amber at this share of the machine; disk never does. */
export const AMBER_SHARE = 0.5;
const GB = 1e9;

export type UsageKind = 'cpu' | 'memory' | 'disk';

export type UsageCell = {
  readonly kind: UsageKind;
  /** "24%", "2.1 GB", or "-" for a null reading. */
  readonly value: string;
  /** The bar's width in percent: `max(2, min(share, 100))`, 0 with no reading. */
  readonly fill: number;
  readonly amber: boolean;
  /** "Not reported yet." on a null reading; null otherwise. */
  readonly tip: string | null;
};

export type JobUsageView =
  | { readonly kind: 'none' }
  | { readonly kind: 'measuring' }
  | {
      readonly kind: 'cells';
      readonly cells: readonly UsageCell[];
      /** "This job uses N% of CPU, …", when every reading is known. */
      readonly title: string | null;
    };

const gbText = (bytes: number, tr: Tr): string =>
  tr('fleet.machines.job_gb', { value: decimal(bytes / GB) });

const fillOf = (pct: number): number => Math.max(2, Math.min(pct, 100));

function noneCell(kind: UsageKind, tr: Tr): UsageCell {
  return {
    kind,
    value: tr('fleet.machines.not_reported'),
    fill: 0,
    amber: false,
    tip: tr('fleet.machines.tip_none'),
  };
}

function cpuCell(pct: number | null, tr: Tr): UsageCell {
  if (pct === null) return noneCell('cpu', tr);
  return {
    kind: 'cpu',
    value: tr('fleet.machines.percent', { value: Math.round(pct) }),
    fill: fillOf(pct),
    amber: pct >= AMBER_SHARE * 100,
    tip: null,
  };
}

/** Memory or disk: bytes against the machine's total, amber only where `amberable`. */
function bytesCell(
  kind: 'memory' | 'disk',
  bytes: number | null,
  total: number | null,
  tr: Tr,
): UsageCell {
  if (bytes === null) return noneCell(kind, tr);
  const share = total !== null && total > 0 ? bytes / total : null;
  return {
    kind,
    value: gbText(bytes, tr),
    fill: share === null ? 0 : fillOf(share * 100),
    amber: kind === 'memory' && share !== null && share >= AMBER_SHARE,
    tip: null,
  };
}

/**
 * A paused job's row (#6753, brief item 108): its process has ended, so CPU reads "0%" and
 * memory and disk are not reported. A zero is the one reading the card knows without a
 * beat; the prototype's estimates never ship.
 */
function pausedCells(tr: Tr): JobUsageView {
  const cpu: UsageCell = {
    kind: 'cpu',
    value: tr('fleet.machines.percent', { value: 0 }),
    fill: 0,
    amber: false,
    tip: null,
  };
  return { kind: 'cells', cells: [cpu, noneCell('memory', tr), noneCell('disk', tr)], title: null };
}

/**
 * One job's usage row (prototype jobRes, machine-card decision 18). None on a machine that
 * does not report job usage, an offline machine, a `last_known` or unknown job; "Measuring
 * usage…" for a `starting` job; a `paused` job's 0% CPU ({@link pausedCells}); else the
 * three cells.
 */
export function jobUsageView(
  job: UtilJob,
  box: Pick<UtilBox, 'reports_job_usage' | 'verdict' | 'specs' | 'stats'>,
  tr: Tr,
): JobUsageView {
  if (box.reports_job_usage !== true || isOffline(box)) return { kind: 'none' };
  const state = jobState(job);
  if (state === 'starting') return { kind: 'measuring' };
  if (state === 'paused') return pausedCells(tr);
  if (state !== 'working' && state !== 'pausing') return { kind: 'none' };
  const u = job.usage;
  const cpu = u?.cpu_pct ?? null;
  const mem = u?.mem_bytes ?? null;
  const disk = u?.disk_bytes ?? null;
  const cells = [
    cpuCell(cpu, tr),
    bytesCell('memory', mem, box.specs?.mem_bytes ?? null, tr),
    bytesCell('disk', disk, box.stats?.workspaceDisk?.totalBytes ?? null, tr),
  ];
  const title =
    cpu === null || mem === null || disk === null
      ? null
      : tr('fleet.machines.job_usage_title', {
          cpu: Math.round(cpu),
          memory: decimal(mem / GB),
          disk: decimal(disk / GB),
        });
  return { kind: 'cells', cells, title };
}

// ─── The zone ────────────────────────────────────────────────────────────────

/**
 * The Now zone header's count, verbatim from the prototype (`:2459`): one job names its
 * capability ("Coder"), N jobs read "N jobs", ", last known" on an offline machine; no jobs
 * shows nothing (the zone holds the status sentence).
 */
export function zoneCount(box: Pick<UtilBox, 'jobs' | 'verdict'>, tr: Tr): string | null {
  const jobs = boxJobs(box);
  const first = jobs[0];
  if (first === undefined) return null;
  if (jobs.length === 1) return tr(capabilityKey(first.role));
  return isOffline(box)
    ? tr('fleet.machines.job_count_last_known', { count: jobs.length })
    : tr.plural('fleet.machines.card_jobs', jobs.length, { count: jobs.length });
}

export type JobGroup = {
  /** The capability key: one group per key, in first-seen order. */
  readonly key: string;
  readonly name: string;
  /** "1 job" / "N jobs". */
  readonly count: string;
  /** "CPU N% · N.N GB" over the known readings; null with none, or offline. */
  readonly usage: string | null;
  /** The accent dot with the slow pulse: any job in the group is working or starting. */
  readonly live: boolean;
  readonly jobs: readonly UtilJob[];
};

/** A group's summed usage, skipping every null reading; null when nothing is known. */
function groupUsage(
  jobs: readonly UtilJob[],
  box: Pick<UtilBox, 'reports_job_usage' | 'verdict'>,
  tr: Tr,
): string | null {
  if (box.reports_job_usage !== true || isOffline(box)) return null;
  let cpu: number | null = null;
  let mem: number | null = null;
  for (const j of jobs) {
    const u = j.usage;
    if (u === null) continue;
    if (u.cpu_pct !== null) cpu = (cpu ?? 0) + u.cpu_pct;
    if (u.mem_bytes !== null) mem = (mem ?? 0) + u.mem_bytes;
  }
  if (cpu === null && mem === null) return null;
  return tr('fleet.machines.job_group_usage', {
    cpu:
      cpu === null
        ? tr('fleet.machines.not_reported')
        : tr('fleet.machines.percent', { value: Math.round(cpu) }),
    memory: mem === null ? tr('fleet.machines.not_reported') : gbText(mem, tr),
  });
}

/** The jobs grouped by capability, in first-seen order (prototype jobsHTML, `:2196-2222`). */
export function jobGroups(
  box: Pick<UtilBox, 'jobs' | 'reports_job_usage' | 'verdict'>,
  tr: Tr,
): readonly JobGroup[] {
  const byKey = new Map<string, UtilJob[]>();
  for (const job of boxJobs(box)) {
    const key = capabilityKey(job.role);
    const list = byKey.get(key);
    if (list === undefined) byKey.set(key, [job]);
    else list.push(job);
  }
  return [...byKey].map(([key, jobs]) => ({
    key,
    name: tr(key),
    count: tr.plural('fleet.machines.card_jobs', jobs.length, { count: jobs.length }),
    usage: groupUsage(jobs, box, tr),
    live: jobs.some((j) => {
      const s = jobState(j);
      return s === 'working' || s === 'starting';
    }),
    jobs,
  }));
}

/** The folded zone's one line: "Coder ×2 · Reviewer" (U+00D7, U+00B7). */
export function summaryLine(
  box: Pick<UtilBox, 'jobs' | 'reports_job_usage' | 'verdict'>,
  tr: Tr,
): string {
  return jobGroups(box, tr)
    .map((g) =>
      g.jobs.length > 1
        ? tr('fleet.machines.job_summary_repeat', { name: g.name, count: g.jobs.length })
        : g.name,
    )
    .join(' · ');
}

/** A job's title, "Untitled job" when its envelope carried none. */
export const jobTitle = (job: Pick<UtilJob, 'title'>, tr: Tr): string =>
  job.title ?? tr('fleet.machines.job_untitled');

/** How long the job has run ("4m"), off its run's first event; null before one. */
export const jobDuration = (job: Pick<UtilJob, 'started_at'>, nowMs: number): string | null =>
  job.started_at === null ? null : since(job.started_at, nowMs);

/** The job's epic label ("Epic: Billing webhooks overhaul"), null outside an epic. */
export const epicLabel = (job: Pick<UtilJob, 'epic'>, tr: Tr): string | null =>
  job.epic == null ? null : tr('fleet.machines.job_epic', { title: job.epic.title });

/** The one-job layout's sub line: "[Epic: …] · <repo> · for <duration>", or null. */
export function jobSubLine(job: UtilJob, nowMs: number, tr: Tr): string | null {
  const parts: string[] = [];
  const epic = epicLabel(job, tr);
  if (epic !== null) parts.push(epic);
  if (job.repo !== null) parts.push(job.repo);
  const dur = jobDuration(job, nowMs);
  if (dur !== null) parts.push(tr('fleet.machines.card_for', { time: dur }));
  return parts.length === 0 ? null : parts.join(' · ');
}

/**
 * The card's epics, folded from its jobs (#6745): one {@link CardEpic} per distinct
 * `plan_id`, in the order its first job appears. `null` (not reported, so the card claims
 * nothing) when the api sent no jobs field, when the machine holds no job, or when a job
 * carries no `epic` field at all; `[]` when every job reported `epic: null` (the data IS
 * reported, so "No epic assigned" is true).
 */
export function cardEpicsFromJobs(box: Pick<UtilBox, 'jobs'>): readonly CardEpic[] | null {
  const jobs = boxJobs(box);
  if (jobs.length === 0 || jobs.some((j) => j.epic === undefined)) return null;
  const out = new Map<string, CardEpic>();
  for (const j of jobs) {
    const e = j.epic;
    if (e == null || out.has(e.plan_id)) continue;
    out.set(e.plan_id, {
      plan_id: e.plan_id,
      title: e.title,
      done: e.done,
      total: e.total,
      running: e.running,
      // #6753: copied only when reported, so an absent field still claims nothing.
      ...(e.paused_at === undefined ? {} : { paused_at: e.paused_at }),
      ...(e.llm === undefined ? {} : { llm: e.llm }),
    });
  }
  return [...out.values()];
}
